import React, { useEffect, useRef, useState } from 'react';
import { parseScan } from '../../utils/imei';

const cameraSupported = () => typeof window !== 'undefined' && 'BarcodeDetector' in window && !!navigator?.mediaDevices?.getUserMedia;

/**
 * IMEI entry for USB/Bluetooth scanners (keyboard wedge), manual typing or the browser camera (BarcodeDetector).
 * Uses the shared strict parser (15 digits + Luhn, CR/LF/Tab trimmed); Enter/Tab never submit the outer form.
 */
const ImeiScanInput = ({ label, disabled, onImei, autoFocus }) => {
    const inputRef = useRef(null);
    const videoRef = useRef(null);
    const streamRef = useRef(null);
    const [error, setError] = useState('');
    const [camera, setCamera] = useState(false);
    const canCamera = cameraSupported();

    useEffect(() => {
        if (autoFocus && inputRef.current) inputRef.current.focus();
    }, [autoFocus]);

    const stopCamera = () => {
        if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
        setCamera(false);
    };
    useEffect(() => stopCamera, []);

    const submit = (value) => {
        const { imei, error: e } = parseScan(value);
        if (e) {
            setError(e);
            setTimeout(() => inputRef.current && inputRef.current.select(), 0);
            return;
        }
        setError('');
        if (inputRef.current) inputRef.current.value = '';
        onImei(imei);
    };

    const onKeyDown = (e) => {
        const isEnter = e.key === 'Enter';
        const isTab = e.key === 'Tab' && !e.shiftKey && String(e.currentTarget.value).trim() !== '';
        if (!isEnter && !isTab) return;
        if (e.nativeEvent && e.nativeEvent.isComposing) return;
        e.preventDefault();
        if (e.repeat || String(e.currentTarget.value).trim() === '' || disabled) return;
        submit(e.currentTarget.value);
    };

    const startCamera = async () => {
        try {
            const detector = new window.BarcodeDetector();
            const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } });
            streamRef.current = stream;
            setCamera(true);
            setTimeout(async () => {
                const video = videoRef.current;
                if (!video) return;
                video.srcObject = stream;
                await video.play();
                const tick = async () => {
                    if (!streamRef.current) return;
                    try {
                        const codes = await detector.detect(video);
                        const hit = codes.map((c) => c.rawValue).find((v) => !parseScan(v).error);
                        if (hit) {
                            stopCamera();
                            submit(hit);
                            return;
                        }
                    } catch (e) {
                        /* keep scanning */
                    }
                    requestAnimationFrame(tick);
                };
                tick();
            }, 0);
        } catch (e) {
            stopCamera();
            setError(`Camera unavailable: ${e.message}`);
        }
    };

    return (
        <div>
            <label className="form-label mb-1" htmlFor={`imei-${label}`}>{label}</label>
            <div className="d-flex">
                <input
                    id={`imei-${label}`}
                    ref={inputRef}
                    className={`form-control ${error ? 'is-invalid' : ''}`}
                    placeholder="Scan or type the 15-digit IMEI, then Enter"
                    autoComplete="off"
                    inputMode="numeric"
                    disabled={disabled}
                    onKeyDown={onKeyDown}
                    onChange={() => error && setError('')}
                />
                <button type="button" className="btn btn-outline-primary btn-sm ms-2" disabled={disabled} onClick={() => submit(inputRef.current.value)}>
                    Look up
                </button>
                {canCamera && (
                    <button type="button" className="btn btn-outline-secondary btn-sm ms-2" disabled={disabled} onClick={camera ? stopCamera : startCamera} title="Scan with camera">
                        <i className="fas fa-camera"></i> {camera ? 'Stop' : 'Camera'}
                    </button>
                )}
            </div>
            {error && <div className="text-danger small mt-1" role="alert">{error}</div>}
            {camera && <video ref={videoRef} muted playsInline style={{ width: 320, maxWidth: '100%', marginTop: 8 }} />}
        </div>
    );
};

export default ImeiScanInput;
